// ** React Import
import React, { useContext, useEffect, useState } from 'react';

// ** Reactstrap  Import
import { Card } from 'reactstrap';

// ** Custom Component
import Chart from 'react-apexcharts';

// ** React Icons Import
import { Circle } from 'react-feather';
import { TbCircleCheckFilled } from 'react-icons/tb';

// ** Context
import { ThemeColors } from '@src/utility/context/ThemeColors';

const CompleteProfile = ({ store }) => {
  const [generalInfoCompletion, setGeneralInfoCompletion] = useState(9);
  const { colors } = useContext(ThemeColors);
  const { fullName, email, phone, phoneSecondary, gender, dob, weight, height, size } =
    store?.profile || [];

  const requiredFields = [
    fullName,
    email,
    phone,
    phoneSecondary,
    gender,
    dob,
    weight,
    height,
    size
  ];
  const completedFields = requiredFields.filter((field) => field !== '');

  const isProfilePhotoExists = store?.profile && store?.profile?.photo;
  const isBannerImage = store?.community && store?.community?.banner;
  const isProfileValid =
    store?.profile &&
    completedFields.length != null &&
    completedFields.length !== '' &&
    completedFields.length === requiredFields.length;

  // Calculate completion percentages based on conditions
  const generalInfoPercentage = isProfileValid
    ? (completedFields.length / requiredFields.length) * 40.0
    : 0;
  const myCardPercentage = store?.profile?.payments && store?.profile?.payments.length > 0 ? 20 : 0;
  const profilePhotoPercentage = isProfilePhotoExists ? 20 : 0;
  const bannerImagePercentage = isBannerImage ? 20 : 0;

  // Update completionPercentage based on the conditions you provided
  const completionPercentage =
    generalInfoPercentage + myCardPercentage + profilePhotoPercentage + bannerImagePercentage;
  const roundedCompletionPercentage = completionPercentage.toFixed();

  const options = {
    series: [76],
    chart: {
      type: 'radialBar',
      offsetY: -20,
      sparkline: {
        enabled: true
      }
    },
    plotOptions: {
      radialBar: {
        startAngle: -95,
        endAngle: 95,
        hollow: {
          margin: 15,
          size: '80%'
        },
        track: {
          background: '#e7e7e7',
          strokeWidth: '97%',
          margin: 5,
          dropShadow: {
            enabled: false,
            top: 2,
            left: 0,
            color: '#999',
            opacity: 1,
            blur: 1
          }
        },
        dataLabels: {
          name: {
            show: false
          },
          value: {
            offsetY: -2,
            fontSize: '22px'
          }
        }
      }
    },
    colors: [colors.success.main],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        shadeIntensity: 0.1,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 50, 53, 91]
      }
    },
    grid: {
      padding: {
        top: 10
      }
    },
    labels: ['Completed']
  },
    // series = [80];
    series = [roundedCompletionPercentage];

  useEffect(() => {
    const interval = setInterval(() => {
      setGeneralInfoCompletion((prevCompletion) =>
        prevCompletion < 6 ? prevCompletion + 1 : prevCompletion
      );

      // Stop the interval when generalInfoCompletion reaches 6
      if (generalInfoCompletion === 6) {
        clearInterval(interval);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [generalInfoCompletion]); // Include generalInfoCompletion as a dependency

  return (
    <Card className="mb-50 px-1 py-2">
      <p className="text-start m-1 h3 fw-bolder">Complete your profile</p>
      <div className="center">
        <Chart
          options={options}
          series={series}
          value={60}
          type="radialBar"
          height={280}
          id="support-tracker-card"
        />
      </div>
      <div className="d-flex flex-column mt-2">
        <div
          className={`d-flex flex-row justify-content-between align-items-center mx-2 h4 ${isProfileValid ? 'text-success' : 'text-muted'
            }`}
        >
          <span>
            {isProfileValid ? (
              <TbCircleCheckFilled size={24} className="me-1" />
            ) : (
              <Circle size={24} className="me-1" />
            )}
            General Information
          </span>
          <span className="me-2"> {`${completedFields.length}/${requiredFields.length}`}</span>
        </div>
        <span
          className={`text-start ms-75 ps-2 h6 mb-50 ${isProfileValid ? 'text-success' : 'text-muted'
            }`}
        >
          |
        </span>
        <div
          className={`d-flex flex-row justify-content-between align-items-center mx-2 h4 ${myCardPercentage ? 'text-success' : 'text-muted'
            }`}
        >
          <span>
            {myCardPercentage ? (
              <TbCircleCheckFilled size={24} className="me-1" />
            ) : (
              <Circle size={24} className="me-1" />
            )}
            My Card
          </span>
          {myCardPercentage ? (
            <span className="me-2"> 1/1 </span>
          ) : (
            <span className="me-2"> 0/1 </span>
          )}
        </div>
        <span
          className={`text-start ms-75 ps-2 h6 mb-50 ${myCardPercentage ? 'text-success' : 'text-muted'
            }`}
        >
          |
        </span>
        <div
          className={`d-flex flex-row justify-content-between align-items-center mx-2 h4 ${isProfilePhotoExists ? 'text-success' : 'text-muted'
            }`}
        >
          <span>
            {isProfilePhotoExists ? (
              <TbCircleCheckFilled size={24} className="me-1" />
            ) : (
              <Circle size={24} className="me-1" />
            )}
            Profile Photo
          </span>
          <span className="me-2">
            {isProfilePhotoExists ? (
              <span className="text-success">1/1</span>
            ) : (
              <span className="text-muted">0/1</span>
            )}
          </span>
        </div>
        <span
          className={`text-start ms-75 ps-2 h6 mb-50 ${isProfilePhotoExists ? 'text-success' : 'text-muted'
            }`}
        >
          |
        </span>
        <div
          className={`d-flex flex-row justify-content-between align-items-center mx-2 h4 ${isBannerImage ? 'text-success' : 'text-muted'
            }`}
        >
          <span>
            {isBannerImage ? (
              <TbCircleCheckFilled size={24} className="me-1" />
            ) : (
              <Circle size={24} className="me-1" />
            )}
            Cover Photo
          </span>
          <span className="me-2">
            {isBannerImage ? (
              <span className="text-success">1/1</span>
            ) : (
              <span className="text-muted">0/1</span>
            )}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default CompleteProfile;
